// Pure history-reducer functions for the place-selection draw toolbar.
//
// These are intentionally free of any Vue/component or MapboxDraw dependencies
// so that fast-check property tests can import and exercise them directly.
// State is treated as immutable input; every transition returns a new
// { history, index } object and never mutates the incoming history array.
// Feature: place-selection-draw-toolbar (Task 1.1)

const DEFAULT_MAX_HISTORY = 50;

/**
 * Record a new snapshot into the history stack.
 *
 * Drops the redo tail (any entries after the current index), appends the new
 * snapshot, and points the index at the last entry. When appending would push
 * the stack beyond `max` entries, the oldest entry is dropped and the index is
 * clamped to the last index.
 *
 * @param {{ history: Array, index: number }} state - Current history state.
 * @param {*} snapshot - The snapshot to append.
 * @param {number} [max=50] - Maximum number of retained entries.
 * @returns {{ history: Array, index: number }} New history state.
 */
export function recordSnapshot(state, snapshot, max = DEFAULT_MAX_HISTORY) {
  const prevHistory = (state && state.history) || [];
  const prevIndex = state && typeof state.index === "number" ? state.index : -1;

  // Discard any redo tail before appending the new snapshot.
  let history = prevHistory.slice(0, prevIndex + 1);
  history.push(snapshot);

  // Enforce the cap by dropping the oldest entries.
  if (history.length > max) {
    history = history.slice(history.length - max);
  }

  return { history, index: history.length - 1 };
}

/**
 * Move one step back in history. When the index is greater than zero it is
 * decremented; otherwise the state is returned unchanged. The history array is
 * never mutated.
 *
 * @param {{ history: Array, index: number }} state - Current history state.
 * @returns {{ history: Array, index: number }} New history state.
 */
export function undo(state) {
  const history = (state && state.history) || [];
  const index = state && typeof state.index === "number" ? state.index : -1;

  if (index > 0) {
    return { history: history.slice(), index: index - 1 };
  }
  return { history: history.slice(), index };
}

/**
 * Move one step forward in history. When the index is before the last entry it
 * is incremented; otherwise the state is returned unchanged. The history array
 * is never mutated.
 *
 * @param {{ history: Array, index: number }} state - Current history state.
 * @returns {{ history: Array, index: number }} New history state.
 */
export function redo(state) {
  const history = (state && state.history) || [];
  const index = state && typeof state.index === "number" ? state.index : -1;

  if (index < history.length - 1) {
    return { history: history.slice(), index: index + 1 };
  }
  return { history: history.slice(), index };
}

/**
 * Determine whether an undo operation is available.
 *
 * @param {{ history: Array, index: number }} state - Current history state.
 * @returns {boolean} True when the index is greater than zero.
 */
export function canUndo(state) {
  const index = state && typeof state.index === "number" ? state.index : -1;
  return index > 0;
}

/**
 * Determine whether a redo operation is available.
 *
 * @param {{ history: Array, index: number }} state - Current history state.
 * @returns {boolean} True when the index is within bounds and before the last entry.
 */
export function canRedo(state) {
  const history = (state && state.history) || [];
  const index = state && typeof state.index === "number" ? state.index : -1;
  return index >= 0 && index < history.length - 1;
}

/**
 * Return the snapshot at the current index, or null when the stack is empty or
 * the index is out of range.
 *
 * @param {{ history: Array, index: number }} state - Current history state.
 * @returns {*|null} The current snapshot or null.
 */
export function current(state) {
  const history = (state && state.history) || [];
  const index = state && typeof state.index === "number" ? state.index : -1;

  if (index < 0 || index >= history.length) {
    return null;
  }
  return history[index];
}

/**
 * Produce an empty history state.
 *
 * @returns {{ history: Array, index: number }} A fresh, empty history state.
 */
export function reset() {
  return { history: [], index: -1 };
}
